import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { isRecoveryHash } from './authUtils.js';
import { supabase } from './supabase.js';

export type Role = 'super_admin' | 'owner' | 'admin' | 'manager' | 'cashier' | 'accountant' | 'anon';

export interface AuthState {
  session: Session | null;
  loading: boolean;
  role: Role;
  clientId: string | null;
  needsPasswordReset: boolean;
  clearPasswordRecovery: () => void;
}

function readRole(session: Session | null): Role {
  const role = session?.user.app_metadata?.role;
  if (
    role === 'super_admin' ||
    role === 'owner' ||
    role === 'admin' ||
    role === 'manager' ||
    role === 'cashier' ||
    role === 'accountant'
  )
    return role;
  return 'anon';
}

function readClientId(session: Session | null): string | null {
  return (session?.user.app_metadata?.client_id as string | undefined) ?? null;
}

/** Track the current Supabase auth session plus the caller's role + client. */
export function useAuth(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(isRecoveryHash);

  const clearPasswordRecovery = useCallback(() => {
    setNeedsPasswordReset(false);
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      if (data.session && isRecoveryHash()) setNeedsPasswordReset(true);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      if (event === 'PASSWORD_RECOVERY' || (next && isRecoveryHash())) {
        setNeedsPasswordReset(true);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return {
    session,
    loading,
    role: readRole(session),
    clientId: readClientId(session),
    needsPasswordReset,
    clearPasswordRecovery,
  };
}
